import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Textarea } from '@artosera/ui';
import {
  pipelineApi,
  useAttente,
  useActionPipeline,
  type PlaceAttente,
} from '../../api/pipeline.js';
import { useOffres } from '../../api/offres.js';
import { galerieAModule } from '../../auth/sujet.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ChoixContact } from './ChoixContact.js';
import styles from './Pipeline.module.css';

export function ListeAttente({
  oeuvreId,
  exemplaireId,
  ajoutPossible = false,
}: {
  oeuvreId: string;
  exemplaireId?: string;
  ajoutPossible?: boolean;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const noteId = useId();
  const [contact, setContact] = useState<{ id: string; nom: string } | null>(null);
  const [note, setNote] = useState('');
  const liste = useAttente(galerieSlug, { oeuvreId, ...(exemplaireId ? { exemplaireId } : {}) });
  const places = liste.data?.places.filter((p) => p.exemplaireId === (exemplaireId ?? null)) ?? [];
  const actives = places.filter((p) => p.etat !== 'close');
  const closes = places.filter((p) => p.etat === 'close');
  const offres = useOffres(
    galerieSlug,
    { oeuvreId, etat: 'acceptee' },
    {
      enabled:
        galerieAModule(galerie, 'offres-relances') && galerieALaction(galerie, 'offers:read'),
    },
  );
  const date = new Date().toISOString().slice(0, 10);
  const offreAcceptee = offres.data?.offres.some(
    (o) =>
      o.validiteJusquau >= date &&
      o.lignes.some((l) => l.oeuvreId === oeuvreId && l.exemplaireId === (exemplaireId ?? null)),
  );
  const ecrire = galerieALaction(galerie, 'pipeline:write');
  const mutation = useActionPipeline(
    galerieSlug,
    (
      geste: { type: 'ajout' } | { type: 'ordre'; ids: string[] } | { type: 'retrait'; id: string },
      appel,
    ) => {
      if (geste.type === 'ajout')
        return pipelineApi
          .ajouterAttente(
            galerieSlug,
            { oeuvreId, exemplaireId, contactId: contact!.id, note },
            appel,
          )
          .then(() => {});
      if (geste.type === 'ordre')
        return pipelineApi.ordonnerAttente(
          galerieSlug,
          { oeuvreId, exemplaireId, ids: geste.ids },
          appel,
        );
      return pipelineApi.retirerAttente(galerieSlug, geste.id, appel);
    },
  );
  function deplacer(id: string, index: number) {
    const ids = actives.map((p) => p.id);
    const position = ids.indexOf(id);
    if (
      position < 0 ||
      index < 0 ||
      index >= ids.length ||
      position === index ||
      mutation.isPending
    )
      return;
    ids.splice(position, 1);
    ids.splice(index, 0, id);
    mutation.mutate({ type: 'ordre', ids });
  }
  function ligne(place: PlaceAttente, index: number, close: boolean) {
    return (
      <li
        key={place.id}
        className={styles.bloc}
        draggable={!close && ecrire && !mutation.isPending}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', place.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(e) => {
          if (!close && ecrire) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (!close && ecrire) deplacer(e.dataTransfer.getData('text/plain'), index);
        }}
      >
        <Link to={`/${galerieSlug}/contacts/${place.contactId}`}>
          {place.contactNom || t('regie:pipeline.contactEfface')}
        </Link>
        {place.etat === 'a_rappeler' ? (
          <Badge tone="reserved">{t('regie:pipeline.aRappeler')}</Badge>
        ) : null}
        {place.note ? <p>{place.note}</p> : null}
        {!close && ecrire ? (
          <div className={styles.gestes}>
            <Button
              variant="ghost"
              disabled={index === 0 || mutation.isPending}
              aria-label={t('regie:pipeline.monterContact', { nom: place.contactNom })}
              onClick={() => deplacer(place.id, index - 1)}
            >
              {t('regie:pipeline.monter')}
            </Button>
            <Button
              variant="ghost"
              disabled={index === actives.length - 1 || mutation.isPending}
              aria-label={t('regie:pipeline.descendreContact', { nom: place.contactNom })}
              onClick={() => deplacer(place.id, index + 1)}
            >
              {t('regie:pipeline.descendre')}
            </Button>
            <Button
              variant="ghost"
              disabled={mutation.isPending}
              aria-label={t('regie:pipeline.retirerContact', { nom: place.contactNom })}
              onClick={() => mutation.mutate({ type: 'retrait', id: place.id })}
            >
              {t('regie:pipeline.retirer')}
            </Button>
          </div>
        ) : null}
      </li>
    );
  }
  return (
    <section className={styles.prevision} aria-label={t('regie:pipeline.attente')}>
      <h2>{t('regie:pipeline.attente')}</h2>
      {liste.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      <ol>{actives.map((p, i) => ligne(p, i, false))}</ol>
      {liste.isSuccess && (ajoutPossible || offreAcceptee) && ecrire && !closes.length ? (
        <div className={styles.formulaire}>
          <ChoixContact valeur={contact} onChange={setContact} />
          <Field htmlFor={noteId} label={t('regie:pipeline.note')}>
            <Textarea
              id={noteId}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <Button
            disabled={!contact}
            loading={mutation.isPending}
            onClick={() =>
              void mutation
                .mutateAsync({ type: 'ajout' })
                .then(() => {
                  setContact(null);
                  setNote('');
                })
                .catch(() => {})
            }
          >
            {t('regie:pipeline.ajouterAttente')}
          </Button>
        </div>
      ) : null}
      {closes.length ? (
        <>
          <h3>{t('regie:pipeline.attendaientOeuvre')}</h3>
          <ol>{closes.map((p, i) => ligne(p, i, true))}</ol>
        </>
      ) : null}
      {mutation.isError ? (
        <p role="alert">
          {estApiError(mutation.error) && mutation.error.status === 409
            ? t('regie:pipeline.conflit')
            : t('commun:erreurReseau')}
        </p>
      ) : null}
    </section>
  );
}
export function AttenteArtiste({ artisteId }: { artisteId: string }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const liste = useAttente(galerieSlug, { artisteId, etat: 'close' });
  if (liste.isError) return <p role="alert">{t('commun:erreurReseau')}</p>;
  const contacts = [
    ...new Map(
      liste.data?.places.filter((p) => !p.contactEfface).map((p) => [p.contactId, p]),
    ).values(),
  ];
  if (!contacts.length) return null;
  return (
    <section className={styles.prevision}>
      <h2>{t('regie:pipeline.attendaientArtiste')}</h2>
      <ul>
        {contacts.map((p) => (
          <li key={p.contactId}>
            <Link to={`/${galerieSlug}/contacts/${p.contactId}`}>{p.contactNom}</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
